"use client";

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { submitOrder } from "@/lib/orders-api";
import DepositModal from "./DepositModal";
import { useQuery } from "@tanstack/react-query";
import { fetchOrderbook } from "@/lib/api";
import { previewLimit, previewMarket, type OrderType, type Side } from "@/lib/quote";
import { useBalances } from "@/lib/useBalances";
import { useSession } from "@/lib/useSignIn";

const fmtNgn = (v: number) =>
  v.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUsd = (v: number | string) =>
  Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const seg = "flex-1 py-1.5 text-xs rounded transition-colors";

const OrderTicket = ({ symbol }: { symbol: string }) => {
  const [side, setSide] = useState<Side>("buy");
  const [depositOpen, setDepositOpen] = useState(false);
  const [type, setType] = useState<OrderType>("market");
  const [amount, setAmount] = useState("");
  const [limitPrice, setLimitPrice] = useState("");

  const { isSignedIn } = useSession();
  const { usd, ngn } = useBalances();
  const qc = useQueryClient();

  const place = useMutation({
    mutationFn: () =>
      submitOrder({
        side,
        type,
        quantity: amount,
        ...(type === "limit" ? { price: limitPrice } : {}),
      }),
    onSuccess: () => {
      setAmount("");
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["balances"] });
    },
  });

  // Reuse the same cached orderbook query the book/ticker use. No extra polling.
  const { data: book } = useQuery({
    queryKey: ["orderbook", symbol],
    queryFn: ({ signal }) => fetchOrderbook(symbol, 12, signal),
    refetchInterval: 4_000,
  });

  const qty = Number(amount);
  const price = Number(limitPrice);

  const preview = useMemo(() => {
    if (type === "market") return previewMarket(book, side, qty);
    return previewLimit(side, qty, price);
  }, [type, book, side, qty, price]);

  // Buy spends NGN, sell spends USD (base). Available balance gates the button.
  const spendCurrency = side === "buy" ? "NGN" : "USD";
  const spendAvailable = side === "buy" ? Number(ngn?.available ?? 0) : Number(usd?.available ?? 0);
  const spendNeeded = side === "buy" ? preview?.total ?? 0 : qty;
  const insufficientBalance = isSignedIn && spendNeeded > spendAvailable;

  const buyStyle = "bg-buy text-black hover:opacity-90";
  const sellStyle = "bg-sell text-white hover:opacity-90";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-3">
      {/* Buy / Sell */}
      <div className="flex gap-1 rounded-md bg-raised p-1">
        <button
          type="button"
          onClick={() => setSide("buy")}
          className={`${seg} ${side === "buy" ? "bg-buy text-black" : "text-fg-muted hover:text-fg"}`}
        >
          Buy USD
        </button>
        <button
          type="button"
          onClick={() => setSide("sell")}
          className={`${seg} ${side === "sell" ? "bg-sell text-white" : "text-fg-muted hover:text-fg"}`}
        >
          Sell USD
        </button>
      </div>

      {/* Market / Limit */}
      <div className="flex gap-3 text-xs" role="tablist">
        {(["market", "limit"] as OrderType[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={type === t}
            onClick={() => setType(t)}
            className={`capitalize ${type === t ? "text-fg" : "text-fg-muted hover:text-fg"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {type === "limit" && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">Limit price (NGN per USD)</span>
          <input
            inputMode="decimal"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder={book ? Number(book.stats.mid_price).toFixed(2) : "0.00"}
            className="tnum rounded-md border border-line bg-page px-2 py-2 text-sm text-fg outline-none focus:border-brand"
          />
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-fg-muted">Amount (USD)</span>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
          placeholder="0.00"
          className="tnum rounded-md border border-line bg-page px-2 py-2 text-sm text-fg outline-none focus:border-brand"
        />
      </label>

      {/* Balance (only meaningful once signed in) */}
      {isSignedIn && (
        <div className="flex justify-between text-[11px] text-fg-muted">
          <span>Available</span>
          <span className="tnum">
            {spendCurrency === "NGN"
              ? `₦${fmtNgn(spendAvailable)}`
              : `$${fmtUsd(spendAvailable)}`}
          </span>
        </div>
      )}

      {/* Live preview */}
      {preview && qty > 0 && (
        <div className="flex flex-col gap-1 rounded-md bg-raised p-2 text-[11px]">
          <div className="flex justify-between text-fg-muted">
            <span>{type === "market" ? "Est. avg price" : "Limit price"}</span>
            <span className="tnum text-fg-secondary">₦{fmtNgn(preview.avgPrice)}</span>
          </div>
          <div className="flex justify-between text-fg-muted">
            <span>{side === "buy" ? "You pay" : "You receive"}</span>
            <span className="tnum text-fg">≈ ₦{fmtNgn(preview.total)}</span>
          </div>
          {preview.insufficientLiquidity && (
            <div className="text-sell">
              Order book only fills ~{Math.round(preview.fillableFraction * 100)}% of this size, 
              the rest would rest or slip.
            </div>
          )}
          {/* Estimate honesty: the book is thin and Daya prices at execution. */}
          <div className="text-fg-muted">Estimate from the live book, final price set at execution.</div>
        </div>
      )}

      {/* Action */}
      {!isSignedIn ? (
        <button
          type="button"
          disabled
          className="cursor-not-allowed rounded-md bg-raised py-2.5 text-sm text-fg-muted"
        >
          Connect a wallet to trade
        </button>
      ) : insufficientBalance ? (
        <button
          type="button"
          onClick={() => setDepositOpen(true)}
          className="rounded-md border border-brand/40 bg-raised py-2.5 text-sm text-fg transition-colors hover:border-brand"
        >
          Insufficient {spendCurrency}, deposit now
        </button>
      ) : (
        <button
          type="button"
          disabled={!preview || qty <= 0 || place.isPending || (type === "limit" && price <= 0)}
          onClick={() => place.mutate()}
          className={`rounded-md py-2.5 text-sm font-medium disabled:opacity-40 ${
            side === "buy" ? buyStyle : sellStyle
          }`}
        >
          {place.isPending ? "Placing…" : `${side === "buy" ? "Buy" : "Sell"} USD`}
        </button>
      )}
      {place.isSuccess && (
        <p className="text-[11px] text-buy">Order placed ✓. Track it in the Orders tab.</p>
      )}
      {place.isError && (
        <p className="text-[11px] text-sell">{(place.error as Error).message}</p>
      )}
      <DepositModal open={depositOpen} onClose={() => setDepositOpen(false)} />
    </div>
  );
};

export default OrderTicket;
